/**
 * Tidal gates along a route. A "gate" here is a stretch where the stream is
 * setting hard against us at the planned time, the kind of place we'd rather wait
 * for slack or a fair tide than punch through. This walks each leg, reads the
 * stream at the leg's midpoint, and flags the legs where a strong stream is foul
 * (running against our heading). For each flagged leg it then finds the next
 * window when the door opens, by re-using the generic gate finder with an
 * "is the stream NOT foul-and-strong here yet?" test.
 *
 * Pure and injectable: the caller supplies the stream function (the same chart-
 * diamond read the passage simulator uses), so this stays testable.
 */
import { findGateWindows, type GateWindow } from '@/tides/gates';

export type StreamReading = { setDeg: number; rateKn: number };
export type StreamFn = (pos: [number, number], ms: number) => StreamReading;

export interface RouteTidalGate {
  /** Index of the leg (from waypoint[legIndex] to waypoint[legIndex+1]). */
  legIndex: number;
  /** Midpoint of the leg, for centring the chart. [lon, lat]. */
  position: [number, number];
  /** The (foul) stream rate at the planned time, knots. */
  rateKn: number;
  /** The stream set at the planned time, degrees true. */
  setDeg: number;
  /** The next window when the stream eases at this spot, or null within horizon. */
  nextWindow: GateWindow | null;
}

export interface RouteGateOpts {
  /** A foul stream at or above this rate (kn) counts as a gate. Default 1.5. */
  foulThresholdKn?: number;
  /** How wide the "against us" cone is, degrees either side of dead foul. Default 60. */
  foulConeDeg?: number;
  /** How far ahead to look for the next easing window (ms). Default 12 h. */
  horizonMs?: number;
  /** Sampling step for the window walk (minutes). Default 20. */
  stepMin?: number;
}

/** Smallest absolute angle between two bearings, 0..180. */
function angleDiff(a: number, b: number): number {
  const d = Math.abs(((a - b) % 360) + 360) % 360;
  return d > 180 ? 360 - d : d;
}

/** Midpoint of a leg (simple average — legs are short relative to the earth). */
function midpoint(a: [number, number], b: [number, number]): [number, number] {
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
}

/** Leg heading, degrees true (plane approximation, good enough for the cone test). */
function legHeadingDeg(a: [number, number], b: [number, number]): number {
  const dLon = (b[0] - a[0]) * Math.cos(((a[1] + b[1]) / 2) * (Math.PI / 180));
  const dLat = b[1] - a[1];
  return (Math.atan2(dLon, dLat) * (180 / Math.PI) + 360) % 360;
}

/**
 * Find the legs where the stream sets hard against us at `departMs`, each with
 * the next window the stream eases. `departMs` is a coarse single time for the
 * whole route (a planning prompt, not a per-leg ETA simulation).
 */
export function findRouteTidalGates(
  waypoints: [number, number][],
  departMs: number,
  streamFn: StreamFn,
  opts: RouteGateOpts = {},
): RouteTidalGate[] {
  const foulThresholdKn = opts.foulThresholdKn ?? 1.5;
  const foulConeDeg = opts.foulConeDeg ?? 60;
  const horizonMs = opts.horizonMs ?? 12 * 3_600_000;
  const stepMin = opts.stepMin ?? 20;
  const gates: RouteTidalGate[] = [];

  for (let i = 0; i + 1 < waypoints.length; i++) {
    const a = waypoints[i];
    const b = waypoints[i + 1];
    const mid = midpoint(a, b);
    const heading = legHeadingDeg(a, b);
    // The stream is foul when its set is roughly OPPOSITE our heading.
    const foulSet = (heading + 180) % 360;

    const isFoul = (ms: number): boolean => {
      const s = streamFn(mid, ms);
      return s.rateKn >= foulThresholdKn && angleDiff(s.setDeg, foulSet) <= foulConeDeg;
    };

    if (!isFoul(departMs)) continue;
    const now = streamFn(mid, departMs);
    // The door is "open" when the stream is no longer foul-and-strong here.
    const windows = findGateWindows(departMs, departMs + horizonMs, stepMin, (ms) => !isFoul(ms));
    const nextWindow = windows.find((w) => w.endMs >= departMs) ?? null;
    gates.push({ legIndex: i, position: mid, rateKn: now.rateKn, setDeg: now.setDeg, nextWindow });
  }
  return gates;
}
